<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import $ from 'jquery';
import 'trumbowyg';
import 'trumbowyg/dist/plugins/base64/trumbowyg.base64.min.js'; // base64 plugin
import 'trumbowyg/dist/plugins/colors/trumbowyg.colors.min.js';
import 'trumbowyg/dist/plugins/fontfamily/trumbowyg.fontfamily.min.js';
import 'trumbowyg/dist/plugins/fontsize/trumbowyg.fontsize.min.js';
import svgIcons from 'trumbowyg/dist/ui/icons.svg';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  font: {
    type: String,
    default: 'Verdana, Geneva, sans-serif'
  },
  btns: {
    type: Array,
    default: () => [
      ['strong', 'em', 'del'],
      ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'],
      ['list'],
      ['horizontalRule'],
      ['image'],
      ['link'],
      ['foreColor', 'fontsize'],
      ['removeformat'],
      ['fullscreen']
    ]
  }
});

const emit = defineEmits(['update:modelValue']);

const editorId = ref(`trumbowyg-${Math.random().toString(36).substr(2, 9)}`);
const isInitialized = ref(false);

onMounted(() => {
  // Initialize Trumbowyg
  $.trumbowyg.svgPath = svgIcons;

  // Override base64 plugin language strings
  $.trumbowyg.langs.en.base64 = 'Upload image';

  $(`#${editorId.value}`).trumbowyg({
    resetCss: false,
    removeformatPasted: true,
    btnsDef: {
      image: {
        dropdown: ['insertImage', 'base64'],
        ico: 'insertImage',
      },
      base64: {
        title: 'Upload image',
        ico: 'insertImage',
      },
      insertImage: {
        title: 'Insert image URL',
      },
      list: {
        dropdown: ['unorderedList', 'orderedList'],
        ico: 'unorderedList',
        title: 'Lists'
      }
    },
    btns: props.btns,
    plugins: {
      colors: {
        colorList: [
          '040707', '0074D9', '7FDBFF', '39CCCC', 'B10DC9', 'F012BE', '85144b',
          'FF4136', 'FF851B', 'FFDC00', '3D9970', '2ECC40', '01FF70', 'AAA', 'DDD', 'FFF'
        ]
      }
    }
  })
  .on('tbwchange', () => {
    emit('update:modelValue', $(`#${editorId.value}`).trumbowyg('html'));
  });

  // Set initial value
  if (props.modelValue) {
    $(`#${editorId.value}`).trumbowyg('html', props.modelValue);
  }

  isInitialized.value = true;
});

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  if (isInitialized.value) {
    const currentHtml = $(`#${editorId.value}`).trumbowyg('html');
    if (currentHtml !== newValue) {
      $(`#${editorId.value}`).trumbowyg('html', newValue);
    }
  }
});

onBeforeUnmount(() => {
  if (isInitialized.value) {
    try {
      $(`#${editorId.value}`).trumbowyg('destroy');
    } catch (e) {
      // Silently handle if already destroyed
    }
  }
});
</script>

<template>
  <div class="tw-w-full">
    <textarea :id="editorId" />
  </div>
</template>

<style scoped>
@import "trumbowyg/dist/ui/trumbowyg.min.css";
@import "trumbowyg/dist/plugins/colors/ui/trumbowyg.colors.min.css";
.trumbowyg-box {
  margin-top: 0 !important;
}

:deep(.trumbowyg-editor) {
  font-family: v-bind('font');
  min-height: 250px;
}

:deep(.trumbowyg-editor ol),
:deep(.trumbowyg-editor ul) {
  list-style: initial;
  padding: unset;
  padding-inline-start: 40px;
}

:deep(.trumbowyg-editor ul) {
  list-style-type: disc;
  list-style-position: inside;
  line-height: 1.9;
}

:deep(.trumbowyg-editor ol) {
  list-style-type: decimal;
  list-style-position: inside;
  line-height: 1.9;
}

:deep(.trumbowyg-editor p) {
  font-family: inherit !important;
  margin-bottom: 1rem;
}

:deep(.trumbowyg-modal-box .trumbowyg-input-infos label) {
  margin: 0;
}
</style>
